/**
 * @file CategoryFilterDropdown atom — Update-Feed-styled skin over the
 * FixedPositionDropdownShell compound (SE-5528 recomposition).
 * @description Preserves the part names consumed by the update-feed category
 * filter (Backdrop / Panel / Item / ItemText / Trigger / TriggerText) while
 * delegating ALL fixed-position/portal/z-index/measurement mechanics to
 * {@link module:components/atoms/FixedPositionDropdownShell}. The former
 * per-theme hex dictionary (`getDropdownColors`) is gone: chrome is now
 * semantic tokens (`bg-surface-*` / `border-outline-*` / `text-content-*`),
 * which resolve inside document.body portals where NativeWind `dark:`
 * variants do not (CSS-variable-backed tokens flip at the root).
 * @module components/atoms/CategoryFilterDropdown
 */
import React from "react";

import {
  FixedPositionDropdownShellBackdrop,
  FixedPositionDropdownShellItem,
  FixedPositionDropdownShellItemText,
  FixedPositionDropdownShellPanel,
  FixedPositionDropdownShellTrigger,
  type DropdownPanelGeometry,
  type FixedPositionDropdownShellTriggerRef,
} from "../FixedPositionDropdownShell";
import { Text } from "../Text";

import type { AccessibilityState } from "react-native";

/**
 * Ref type for {@link CategoryFilterDropdownTrigger}. Call sites wire the
 * positioning hook's `triggerRef` here rather than importing raw view types
 * (banned outside the atom layer).
 */
export type CategoryFilterDropdownTriggerRef =
  FixedPositionDropdownShellTriggerRef;

// ---------------------------------------------------------------------------
// Layout constants (re-exported for call-site positioning arithmetic)
// ---------------------------------------------------------------------------

/** Trigger height in pixels. */
export const DROPDOWN_TRIGGER_HEIGHT = 36;
/** Trigger width in pixels. */
export const DROPDOWN_TRIGGER_WIDTH = 160;
/** Minimum panel width in pixels. */
export const DROPDOWN_PANEL_MIN_WIDTH = 200;
/** Pixel gap between the trigger's bottom edge and the panel's top edge. */
export const DROPDOWN_PANEL_OFFSET = 4;

// ---------------------------------------------------------------------------
// CategoryFilterDropdownBackdrop
// ---------------------------------------------------------------------------

/** Props for {@link CategoryFilterDropdownBackdrop}. */
export interface CategoryFilterDropdownBackdropProps {
  /** Dismiss handler. */
  readonly onPress: () => void;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label. */
  readonly accessibilityLabel?: string;
}

/**
 * Full-viewport translucent backdrop that dismisses the dropdown on press —
 * a direct passthrough to the shell backdrop (fixed+portal on web, absolute
 * on native, z-index 49).
 * @param props - Component props.
 * @param props.onPress - Dismiss handler.
 * @param props.testID - E2E test identifier.
 * @param props.accessibilityLabel - Accessibility label.
 */
export /**
 *
 */
const CategoryFilterDropdownBackdrop = ({
  onPress,
  testID,
  accessibilityLabel,
}: CategoryFilterDropdownBackdropProps) => (
  <FixedPositionDropdownShellBackdrop
    onPress={onPress}
    testID={testID}
    accessibilityLabel={accessibilityLabel}
  />
);

CategoryFilterDropdownBackdrop.displayName = "CategoryFilterDropdownBackdrop";

// ---------------------------------------------------------------------------
// CategoryFilterDropdownPanel
// ---------------------------------------------------------------------------

/** Props for {@link CategoryFilterDropdownPanel}. */
export interface CategoryFilterDropdownPanelProps {
  /** Style-ready geometry from `useDropdownPositioning` (null = unmeasured). */
  readonly geometry: DropdownPanelGeometry | null;
  /** Native Modal dismiss handler (SE-5568) — see the shell Panel prop. */
  readonly onDismiss?: () => void;
  /** Panel option rows. */
  readonly children: React.ReactNode;
  /** E2E test identifier. */
  readonly testID?: string;
}

/**
 * Floating option panel pinned below the trigger (fixed+portal on web,
 * Modal-hosted on native, z-index 50) with the update-feed vertical inset.
 * @param props - Component props.
 * @param props.geometry - Style-ready geometry (null = not yet measured).
 * @param props.onDismiss - Native Modal dismiss handler (SE-5568).
 * @param props.children - Panel option rows.
 * @param props.testID - E2E test identifier.
 */
export /**
 *
 */
const CategoryFilterDropdownPanel = ({
  geometry,
  onDismiss,
  children,
  testID,
}: CategoryFilterDropdownPanelProps) => (
  <FixedPositionDropdownShellPanel
    geometry={geometry}
    onDismiss={onDismiss}
    testID={testID}
    className="py-1"
  >
    {children}
  </FixedPositionDropdownShellPanel>
);

CategoryFilterDropdownPanel.displayName = "CategoryFilterDropdownPanel";

// ---------------------------------------------------------------------------
// CategoryFilterDropdownItem
// ---------------------------------------------------------------------------

/** Props for {@link CategoryFilterDropdownItem}. */
export interface CategoryFilterDropdownItemProps {
  /** Whether this option is the currently selected value. */
  readonly isSelected: boolean;
  /** Selection handler. */
  readonly onPress: () => void;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label. */
  readonly accessibilityLabel?: string;
  /** Accessibility state forwarded to the Pressable. */
  readonly accessibilityState?: AccessibilityState;
  /** Icon + text content. */
  readonly children: React.ReactNode;
}

/**
 * A single option row inside the dropdown panel. Selected rows tint via the
 * `bg-surface-subtle` token.
 * @param props - Component props.
 * @param props.isSelected - Whether this option is the currently selected value.
 * @param props.onPress - Selection handler.
 * @param props.testID - E2E test identifier.
 * @param props.accessibilityLabel - Accessibility label.
 * @param props.accessibilityState - Accessibility state forwarded to the Pressable.
 * @param props.children - Icon + text content.
 */
export /**
 *
 */
const CategoryFilterDropdownItem = ({
  isSelected,
  onPress,
  testID,
  accessibilityLabel,
  accessibilityState,
  children,
}: CategoryFilterDropdownItemProps) => (
  <FixedPositionDropdownShellItem
    isSelected={isSelected}
    onPress={onPress}
    testID={testID}
    accessibilityLabel={accessibilityLabel}
    accessibilityState={accessibilityState}
  >
    {children}
  </FixedPositionDropdownShellItem>
);

CategoryFilterDropdownItem.displayName = "CategoryFilterDropdownItem";

// ---------------------------------------------------------------------------
// CategoryFilterDropdownItemText
// ---------------------------------------------------------------------------

/** Props for {@link CategoryFilterDropdownItemText}. */
export interface CategoryFilterDropdownItemTextProps {
  /** Whether this option is the currently selected value. */
  readonly isSelected: boolean;
  /** Option label text. */
  readonly children: React.ReactNode;
}

/**
 * Themed option label text. Selected rows use the accent token and medium
 * weight via the shell's ItemText.
 * @param props - Component props.
 * @param props.isSelected - Whether this option is the currently selected value.
 * @param props.children - Option label text.
 */
export /**
 *
 */
const CategoryFilterDropdownItemText = ({
  isSelected,
  children,
}: CategoryFilterDropdownItemTextProps) => (
  <FixedPositionDropdownShellItemText isSelected={isSelected}>
    {children}
  </FixedPositionDropdownShellItemText>
);

CategoryFilterDropdownItemText.displayName = "CategoryFilterDropdownItemText";

// ---------------------------------------------------------------------------
// CategoryFilterDropdownTrigger
// ---------------------------------------------------------------------------

/** Props for {@link CategoryFilterDropdownTrigger}. */
export interface CategoryFilterDropdownTriggerProps {
  /** Whether the panel is currently open. */
  readonly isOpen: boolean;
  /** Toggle handler. */
  readonly onPress: () => void;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label. */
  readonly accessibilityLabel?: string;
  /** Trigger content — label text, optional icon, chevron. */
  readonly children: React.ReactNode;
}

/**
 * Update-feed trigger chrome (36×160 bordered pill) over the shell's
 * measurable trigger. Forward the positioning hook's `triggerRef` so the
 * fixed panel can be anchored to viewport coordinates on web.
 * @param props - Component props.
 * @param props.isOpen - Whether the panel is currently open.
 * @param props.onPress - Toggle handler.
 * @param props.testID - E2E test identifier.
 * @param props.accessibilityLabel - Accessibility label.
 * @param props.children - Trigger content (label text, optional icon, chevron).
 * @param ref - Forwarded ref for viewport-coordinate measurement.
 */
export const CategoryFilterDropdownTrigger = React.forwardRef<
  CategoryFilterDropdownTriggerRef,
  CategoryFilterDropdownTriggerProps
>(({ isOpen, onPress, testID, accessibilityLabel, children }, ref) => (
  <FixedPositionDropdownShellTrigger
    ref={ref}
    isOpen={isOpen}
    onPress={onPress}
    testID={testID}
    accessibilityLabel={accessibilityLabel}
    className="h-9 w-40 shrink-0 flex-row items-center justify-between gap-2 rounded-md border border-outline-default px-3"
  >
    {children}
  </FixedPositionDropdownShellTrigger>
));

CategoryFilterDropdownTrigger.displayName = "CategoryFilterDropdownTrigger";

// ---------------------------------------------------------------------------
// CategoryFilterDropdownTriggerText
// ---------------------------------------------------------------------------

/** Props for {@link CategoryFilterDropdownTriggerText}. */
export interface CategoryFilterDropdownTriggerTextProps {
  /** Maximum lines before truncation. */
  readonly numberOfLines?: number;
  /** Selected option label. */
  readonly children: React.ReactNode;
}

/**
 * Themed trigger label text. Truncates with `numberOfLines` when the selected
 * label is long.
 * @param props - Component props.
 * @param props.numberOfLines - Maximum lines before truncation.
 * @param props.children - Selected option label.
 */
export /**
 *
 */
const CategoryFilterDropdownTriggerText = ({
  numberOfLines,
  children,
}: CategoryFilterDropdownTriggerTextProps) => (
  <Text
    variant="body-strong"
    numberOfLines={numberOfLines}
    className="shrink text-content-secondary"
  >
    {children}
  </Text>
);

CategoryFilterDropdownTriggerText.displayName =
  "CategoryFilterDropdownTriggerText";
